// Lecture mutations that keep the lectures and lecture-tree slices in step
// with the server (ora-fe ADR-0002: the request lives in
// `src/services/lectures.ts`, the dispatch lives here). Dispatch them:
// `await dispatch(updateLecture(id, { name }))` resolves to what the service
// resolved to and rejects with the same error.
import type { ThunkAction, UnknownAction } from '@reduxjs/toolkit';
import {
  deleteLectureById,
  postBulkLecturePreviews,
  postLecture,
  postLectureMove,
  postLectureReorder,
  putLecture,
  putLectureStatus,
  type CreateLectureData,
  type DeleteLectureResult,
  type MoveLectureParams,
  type MoveLectureResult,
  type UpdateLectureData,
} from '../../services/lectures';
import type { RootState } from '../../store/store';
import type {
  LecturePreview,
  LectureTreeNode,
  LectureTreeResponse,
} from '../../types/Lecture';
import {
  nodeMoved,
  nodesRemoved,
  nodeUpserted,
} from '../lectureTree/lectureTreeSlice';
import {
  lectureOrderKeyUpdated,
  lectureOrderUpdated,
  lectureReceived,
  lecturesRemoved,
  lecturesUpsertMany,
} from './lecturesSlice';

export type LectureThunk<Result> = ThunkAction<
  Promise<Result>,
  RootState,
  unknown,
  UnknownAction
>;

const isLectureTreeNode = (
  lecture: LecturePreview | LectureTreeNode
): lecture is LectureTreeNode =>
  'nodeType' in lecture &&
  (lecture.nodeType === 'FOLDER' || lecture.nodeType === 'DOCUMENT');

export const treeDocumentToLecturePreview = (
  node: LectureTreeNode,
  exam?: LectureTreeResponse['exam'],
  ord = 0
): LecturePreview => ({
  id: node.id,
  name: node.name,
  description: node.description,
  // These fields are TYPED as Moment but every other hydration path dispatches
  // the API response verbatim, so the slice actually holds ISO strings. A real
  // Moment here is non-serializable state and trips Redux's dev check on every
  // action that follows (ORA-498). Keep the string the rest of the slice has.
  createdAt: node.createdAt as unknown as LecturePreview['createdAt'],
  // The EFFECTIVE date, not the node's own: this flat entity is display-only
  // for this field, the tree row already shows the inherited date, and the
  // backend's own preview emits it — so without the fallback the preview panel
  // was the one surface showing no date at all for a document that inherits
  // one from its folder (ORA-498).
  date: (node.effectiveDate ??
    node.date ??
    null) as unknown as LecturePreview['date'],
  s3Key: node.mapping?.s3Key ?? undefined,
  ord,
  orderKey: node.orderKey,
  examId: exam?.id ?? node.examId,
  exam: exam ? { id: exam.id, name: exam.name } : undefined,
  enabled: node.enabled,
  // The flat lecture selectors do not understand inherited visibility. Treat
  // effective hidden state as hidden when hydrating document previews.
  hidden: node.effectiveHidden,
  selected: node.selected,
  uploadedAt: node.uploadedAt,
  documentAccessState: node.documentAccessState,
  fileName: node.fileName,
  hasDocument: node.hasDocument,
  videos: node.videos,
  flashcards: node.flashcards,
  vignettes: node.vignettes,
});

// Only ever called with a payload a tree-aware caller ASKED the API for. The
// legacy lecture payload now also carries `nodeType`, so a response can no
// longer be sniffed — mistaking one for a tree node writes `undefined` over
// `hidden`/`s3Key`/`exam`/`hasDocument`… on the flat lecture entity.
const isFolderTreeNode = (
  lecture: LecturePreview | LectureTreeNode
): lecture is LectureTreeNode =>
  isLectureTreeNode(lecture) && lecture.nodeType === 'FOLDER';

const dispatchLectureTreeNode = (
  dispatch: (action: UnknownAction) => unknown,
  node: LectureTreeNode
) => {
  dispatch(nodeUpserted(node));
  if (node.nodeType === 'DOCUMENT') {
    dispatch(lectureReceived(treeDocumentToLecturePreview(node)));
  }
};

/**
 * Create a new lecture
 * @param lectureData - Object containing lecture creation parameters
 * @returns Promise<Lecture> - The created lecture object
 */
export const createLecture =
  (
    lectureData: CreateLectureData,
    {
      isTreeNode = false,
    }: {
      /**
       * Set ONLY by the folder-tree call sites, which know the response is a
       * `LectureTreeNode`. Legacy creates keep dispatching `lectureReceived`
       * verbatim (the legacy payload also carries `nodeType`, so it cannot be
       * distinguished by shape).
       */
      isTreeNode?: boolean;
    } = {}
  ): LectureThunk<LecturePreview | LectureTreeNode> =>
  async (dispatch) => {
    const created = await postLecture(lectureData);

    if (isTreeNode) {
      dispatchLectureTreeNode(dispatch, created as LectureTreeNode);
    } else {
      dispatch(lectureReceived(created as LecturePreview));
    }

    return created;
  };

export const createLectureFolder =
  ({
    examId,
    parentId,
    name,
  }: {
    examId: string;
    parentId: string | null;
    name: string;
  }): LectureThunk<LectureTreeNode> =>
  async (dispatch) => {
    const created = await dispatch(
      createLecture(
        {
          examId,
          parentId,
          name,
          nodeType: 'FOLDER',
        },
        { isTreeNode: true }
      )
    );

    if (!isLectureTreeNode(created) || created.nodeType !== 'FOLDER') {
      throw new Error('Folder creation returned an invalid lecture node.');
    }

    return created;
  };

/**
 * Update an existing lecture
 * @param lectureId - The ID of the lecture to update
 * @param updateData - Object containing lecture update parameters
 * @returns Promise<Lecture> - The updated lecture object
 */
export const updateLecture =
  (
    lectureId: string,
    updateData: UpdateLectureData,
    {
      isTreeNode = false,
    }: {
      /**
       * Set ONLY by a call site that knows the response is a `LectureTreeNode`,
       * exactly as `createLecture` uses it. A FOLDER is routed to the tree
       * whether or not it is set (see below), so this is only needed for a
       * tree-aware caller that wants the same handling for a document.
       */
      isTreeNode?: boolean;
    } = {}
  ): LectureThunk<LecturePreview | LectureTreeNode | undefined> =>
  async (dispatch) => {
    // Immediately update the order number if it's provided
    if (typeof updateData.ord === 'number') {
      dispatch(lectureOrderUpdated({ lectureId, ord: updateData.ord }));
    }

    const updated = await putLecture(lectureId, updateData);

    // `PUT /:lectureId` answers with a LectureTreeNode for a FOLDER and a
    // LecturePreview for a DOCUMENT. A folder must never reach the flat
    // lectures collection: it arrives with a raw-string `date` and the
    // folder's own `hidden`, and then counts as a selectable "document" that
    // the auto-select-first fallback can push into `selectedLectureIds` and
    // the debounced selection write (ORA-498).
    if (updated) {
      if (isTreeNode || isFolderTreeNode(updated)) {
        dispatchLectureTreeNode(dispatch, updated as LectureTreeNode);
      } else {
        dispatch(lectureReceived(updated as LecturePreview));
      }
    }

    return updated;
  };

/**
 * Delete an existing lecture or folder, and drop every removed id from both
 * the flat lectures collection and the tree.
 */
export const deleteLecture =
  (lectureId: string): LectureThunk<DeleteLectureResult> =>
  async (dispatch) => {
    const result = await deleteLectureById(lectureId);

    dispatch(lecturesRemoved(result.deletedIds));
    dispatch(nodesRemoved(result.deletedIds));

    return result;
  };

export const updateLectureStatus =
  ({
    lectureId,
    enabled,
    hidden,
    skipStoreUpdate = false,
  }: {
    lectureId: string;
    enabled?: boolean;
    hidden?: boolean;
    skipStoreUpdate?: boolean;
  }): LectureThunk<LecturePreview> =>
  async (dispatch) => {
    const updated = await putLectureStatus({ lectureId, enabled, hidden });

    if (!skipStoreUpdate) {
      dispatch(lectureReceived(updated));
    }

    return updated;
  };

// Refresh a known set of lecture previews (by id) and upsert them into the
// store without touching the rest of the collection. Unlike
// fetchBulkLecturePreviews, this never falls back to a full-collection
// replace (lecturesReceived) — it's meant for targeted refreshes, e.g.
// after a mapping job finalizes a lecture server-side.
export const refreshLecturePreviewsById =
  (lectureIds: string[]): LectureThunk<LecturePreview[]> =>
  async (dispatch) => {
    const { lectures } = await postBulkLecturePreviews({ lectureIds });

    dispatch(lecturesUpsertMany(lectures));
    return lectures;
  };

/**
 * Reorder a lecture within an exam using dense order keys
 * One network request; returns new orderKey for the moved lecture
 */
export const reorderLecture =
  (
    lectureId: string,
    params: {
      examId: string;
      index?: number;
      beforeId?: string;
      afterId?: string;
    }
  ): LectureThunk<string> =>
  async (dispatch) => {
    const orderKey = await postLectureReorder(lectureId, params);

    dispatch(lectureOrderKeyUpdated({ lectureId, orderKey }));
    return orderKey;
  };

export const moveLecture =
  (
    lectureId: string,
    params: MoveLectureParams
  ): LectureThunk<MoveLectureResult> =>
  async (dispatch) => {
    const result = await postLectureMove(lectureId, params);

    dispatch(nodeMoved(result));
    return result;
  };
